/* eslint-disable @typescript-eslint/no-unused-vars */

import React, { useState, ChangeEvent } from 'react';
import { useViewport } from 'reactflow';
import { useTranslation } from 'react-i18next';
import { store } from 'src/redux/store';
import { setShowNodesType } from 'src/redux/workflows/slice';
import OptionItemList from 'src/components/ui/OptionItemList/OptionItemList';
import { OptionItemAccessory, OptionItemTone } from 'src/components/ui/OptionItem/OptionItem';
import Divider from 'src/components/ui/Divider/Divider';
import Input from '../../ui/Input/Input';
import Icon, { IconName } from '../../ui/Icon/Icon';
import styles from './NodePanel.module.scss';

interface NodeOption {
  id: number;
  icon: string;
  textKey: string;
  type: string;
  nodeType?: string;
}

const nodeOptions: NodeOption[] = [
  { id: 1, icon: 'signPost' as IconName, textKey: 'workflows.condition', type: 'logic', nodeType: 'condition' },
  { id: 2, icon: 'lightning' as IconName, textKey: 'workflows.action', type: 'action', nodeType: 'action' },
];

const NodePanel: React.FC<any> = ({ id, data }) => {
  const { t } = useTranslation();
  const [selectedOption, setSelectedOption] = useState('');
  const localizedOptions = React.useMemo(
    () => nodeOptions.map(option => ({ ...option, text: t(option.textKey) })),
    [t],
  );
  const filteredOptions = localizedOptions.filter(option =>
    option.text.toLowerCase().includes(selectedOption.toLowerCase()),
  );

  const handleSelectedOptionChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(e.target.value);
  };

  const handleDeleteClick = () => {
    store.dispatch(setShowNodesType());
  };

  const handleOptionClick = (option: NodeOption) => {
    const targetPlusNode = data.clickedPlusNodeId || data.largestPlusElement?.id;
    if (data.onAddNode && targetPlusNode) {
      store.dispatch(setShowNodesType());
      data.onAddNode(targetPlusNode, option.nodeType);
    }
  };
  const { zoom } = useViewport();

  return (
    <div className={styles.root} style={{ transform: `scale(${zoom})` }}>
      <div className={styles.content}>
        <div className="py-16 px-20">
          <div className="justify-between flex">
            <h2 className="typo-text-16--bold text-full pb-8">{t('workflows.add_node')}</h2>
            <button type="button" aria-label="Delete" onClick={handleDeleteClick}>
              <Icon name="close" width={20} height={20} />
            </button>
          </div>
          <Input
            value={selectedOption}
            placeholder={t('general.search_placeholder')}
            onChange={handleSelectedOptionChange}
            className="w-full"
            icon="search"
          />
        </div>
        <div className="px-20 pb-16">
          <p className="typo-text-12 text-faded pb-8">{t('workflows.logic')}</p>
          {filteredOptions.filter(o => o.type === 'logic').length > 0 && (
            <OptionItemList
              className="w-full"
              items={filteredOptions
                .filter(option => option.type === 'logic')
                .map(e => ({
                  id: e.id,
                  label: e.text,
                  accessoryItem: e.icon,
                  tone: OptionItemTone.Default,
                  accessory: OptionItemAccessory.Thumbnail,
                  onClick: () => handleOptionClick(e),
                }))}
            />
          )}
          <Divider className="my-8" customColor="border-subtle-transparent" />
          <p className="typo-text-12 text-faded pb-8">{t('workflows.actions')}</p>
          {filteredOptions.filter(o => o.type === 'action').length > 0 && (
            <OptionItemList
              className="w-full"
              items={filteredOptions
                .filter(option => option.type === 'action')
                .map(e => ({
                  id: e.id,
                  label: e.text,
                  accessoryItem: e.icon,
                  tone: OptionItemTone.Default,
                  accessory: OptionItemAccessory.Thumbnail,
                  onClick: () => handleOptionClick(e),
                }))}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default NodePanel;
